var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Chip, } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { jobService } from '@services/jobService';
import { formatDate, formatDateForFilename } from '@utils/dateUtils';
const AdminJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    useEffect(() => {
        loadJobs();
    }, []);
    const loadJobs = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            setError(null);
            // Load all jobs directly (no businessId filter to get all)
            const allJobs = yield jobService.getAll();
            // Sort by most recent first (by createdAt)
            const sortedJobs = allJobs.sort((a, b) => {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
            setJobs(sortedJobs);
        }
        catch (err) {
            console.error('Error loading jobs:', err);
            setError(err.message || 'Failed to load jobs');
        }
        finally {
            setLoading(false);
        }
    });
    const handleExport = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setExporting(true);
            setError(null);
            const response = yield fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/jobs/export`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) {
                throw new Error('Export failed');
            }
            const blob = yield response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `jobs_export_${formatDateForFilename()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }
        catch (err) {
            console.error('Error exporting jobs:', err);
            setError(err.message || 'Failed to export jobs');
        }
        finally {
            setExporting(false);
        }
    });
    const columns = [
        {
            field: 'businessName',
            headerName: 'Business',
            width: 200,
            valueGetter: (value, row) => { var _a; return ((_a = row.business) === null || _a === void 0 ? void 0 : _a.name) || 'Unknown'; },
            renderCell: (params) => (_jsx(Chip, { label: params.value, color: "primary", variant: "outlined", size: "small" })),
        },
        {
            field: 'machineName',
            headerName: 'Machine Name',
            width: 200,
            valueGetter: (value, row) => { var _a; return ((_a = row.machine) === null || _a === void 0 ? void 0 : _a.name) || 'Unknown'; },
        },
        {
            field: 'name',
            headerName: 'Job',
            width: 200,
        },
        {
            field: 'createdAt',
            headerName: 'Date Added',
            width: 150,
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 300,
            flex: 1,
        },
    ];
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }, children: [_jsx(Typography, { variant: "h6", children: "All Jobs" }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(RefreshIcon, {}), onClick: loadJobs, disabled: loading, children: "Refresh" }), _jsx(Button, { variant: "contained", startIcon: _jsx(DownloadIcon, {}), onClick: handleExport, disabled: exporting || jobs.length === 0, children: exporting ? 'Exporting...' : 'Export to Excel' })] })] }), error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: error })), _jsx(Alert, { severity: "info", sx: { mb: 2 }, children: "Complete job listing across all businesses and machines, ordered by date added (most recent first). Use the toolbar to filter and search jobs. Export to Excel for reporting." }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { sx: { height: 600, width: '100%' }, children: _jsx(DataGrid, { rows: jobs, columns: columns, pageSizeOptions: [25, 50, 100], initialState: {
                        pagination: {
                            paginationModel: { pageSize: 25 },
                        },
                    }, slots: { toolbar: GridToolbar }, slotProps: {
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }, disableRowSelectionOnClick: true }) })), _jsx(Box, { sx: { mt: 2 }, children: _jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Total Jobs: ", jobs.length] }) })] }));
};
export default AdminJobs;
